import { useEffect, useRef, useState } from 'react';

interface TouchInteractionOptions {
  onSwipeLeft?: () => void;
  onSwipeRight?: () => void;
  onSwipeUp?: () => void;
  onSwipeDown?: () => void;
  onTap?: () => void;
  enableHaptic?: boolean;
  swipeThreshold?: number;
}

interface TouchState {
  startX: number;
  startY: number;
  startTime: number;
  isSwiping: boolean;
}

export const useTouchInteraction = (options: TouchInteractionOptions = {}) => {
  const {
    onSwipeLeft,
    onSwipeRight,
    onSwipeUp,
    onSwipeDown,
    onTap,
    enableHaptic = true,
    swipeThreshold = 50,
  } = options;

  const touchState = useRef<TouchState>({
    startX: 0,
    startY: 0,
    startTime: 0,
    isSwiping: false,
  });

  const triggerHaptic = () => {
    if (enableHaptic && 'vibrate' in navigator) {
      navigator.vibrate(10);
    }
  };

  const handleTouchStart = (e: TouchEvent) => {
    const touch = e.touches[0];
    touchState.current = {
      startX: touch.clientX,
      startY: touch.clientY,
      startTime: Date.now(),
      isSwiping: false,
    };
  };

  const handleTouchMove = (e: TouchEvent) => {
    if (!touchState.current.startX) return;

    const touch = e.touches[0];
    const deltaX = Math.abs(touch.clientX - touchState.current.startX);
    const deltaY = Math.abs(touch.clientY - touchState.current.startY);

    // Mark as swiping if movement exceeds threshold
    if (deltaX > 10 || deltaY > 10) {
      touchState.current.isSwiping = true;
    }
  };

  const handleTouchEnd = (e: TouchEvent) => {
    const touch = e.changedTouches[0];
    const deltaX = touch.clientX - touchState.current.startX;
    const deltaY = touch.clientY - touchState.current.startY;
    const deltaTime = Date.now() - touchState.current.startTime;

    // Check for tap (quick touch with minimal movement)
    if (!touchState.current.isSwiping && deltaTime < 300 && onTap) {
      triggerHaptic();
      onTap();
      return;
    }

    // Check for swipe gestures
    if (Math.abs(deltaX) > Math.abs(deltaY)) {
      // Horizontal swipe
      if (Math.abs(deltaX) > swipeThreshold) {
        triggerHaptic();
        if (deltaX > 0 && onSwipeRight) {
          onSwipeRight();
        } else if (deltaX < 0 && onSwipeLeft) {
          onSwipeLeft();
        }
      }
    } else {
      // Vertical swipe
      if (Math.abs(deltaY) > swipeThreshold) {
        triggerHaptic();
        if (deltaY > 0 && onSwipeDown) {
          onSwipeDown();
        } else if (deltaY < 0 && onSwipeUp) {
          onSwipeUp();
        }
      }
    }

    // Reset state
    touchState.current = {
      startX: 0,
      startY: 0,
      startTime: 0,
      isSwiping: false,
    };
  };

  return {
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
    triggerHaptic,
  };
};

// Hook for attaching touch handlers to a ref
export const useTouchHandlers = (
  options: TouchInteractionOptions = {}
) => {
  const ref = useRef<HTMLElement>(null);
  const { handleTouchStart, handleTouchMove, handleTouchEnd } = useTouchInteraction(options);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    element.addEventListener('touchstart', handleTouchStart, { passive: true });
    element.addEventListener('touchmove', handleTouchMove, { passive: true });
    element.addEventListener('touchend', handleTouchEnd, { passive: true });

    return () => {
      element.removeEventListener('touchstart', handleTouchStart);
      element.removeEventListener('touchmove', handleTouchMove);
      element.removeEventListener('touchend', handleTouchEnd);
    };
  }, [handleTouchStart, handleTouchMove, handleTouchEnd]);

  return ref;
};

// Utility to check if device supports touch
export const isTouchDevice = (): boolean => {
  return (
    'ontouchstart' in window ||
    navigator.maxTouchPoints > 0 ||
    (navigator as any).msMaxTouchPoints > 0
  );
};
